import type { IntentNodeId } from "app/pages/sales-intelligence/domain/intent/types/taxonomy";
import type { TechnologyNodeId } from "app/pages/sales-intelligence/domain/technology/types/taxonomy";
import { trackError } from "app/pages/sales-intelligence/utils/errors/tracking";
import { SiError, errorCategoryMap } from "app/pages/sales-intelligence/utils/errors/types";
import { positionalNodeLevelOf } from "app/pages/sales-intelligence/utils/taxonomy-tree/nodeId";
import { canonicaliseValues } from "app/pages/sales-intelligence/utils/taxonomy-tree/tree";
import type { TaxonomyIndex } from "app/pages/sales-intelligence/utils/taxonomy-tree/types";

import type { SignalEnrichmentFiltersPayload } from "../types/model";

/** An index and a selection taken against it, so the two cannot be paired up wrongly at a call site. */
type SignalFilterSelection<TId extends string> = {
    readonly index: TaxonomyIndex<TId>;
    readonly ids: readonly TId[];
};

/** Both taxonomies are exactly three deep; the caller names the buckets it gets back. */
type LevelBuckets = readonly [readonly string[], readonly string[], readonly string[]];

const TAXONOMY_DEPTH = 3;

/**
 * Buckets a canonical selection by node depth.
 *
 * The mapping is total. Levels are backend-shaped rather than ours, so a taxonomy that grows a fourth
 * one would otherwise fold silently into the deepest bucket and send a wrong payload — not a shape
 * TypeScript can catch, which is why the overflow is reported rather than absorbed.
 */
const bucketByLevel = <TId extends string>(
    { index, ids }: SignalFilterSelection<TId>,
    taxonomyName: string,
): LevelBuckets => {
    const buckets: string[][] = Array.from({ length: TAXONOMY_DEPTH }, () => []);

    canonicaliseValues(ids, index).forEach((id) => {
        const node = index.byId.get(id);

        // `canonicaliseValues` deliberately preserves ids it cannot resolve, so they reach this point.
        if (!node) {
            return;
        }

        const level = positionalNodeLevelOf(id);

        if (level >= TAXONOMY_DEPTH) {
            trackError(
                new SiError(`Signal filter node below the known depth of the ${taxonomyName} taxonomy`, {
                    category: errorCategoryMap.invariant,
                    operation: "buildSignalFiltersPayload",
                }),
            );

            return;
        }

        buckets[level].push(node.name);
    });

    return [buckets[0], buckets[1], buckets[2]];
};

type BuildSignalFiltersPayloadParams = {
    readonly intent?: SignalFilterSelection<IntentNodeId>;
    readonly technology?: SignalFilterSelection<TechnologyNodeId>;
};

export const buildSignalFiltersPayload = ({
    intent,
    technology,
}: BuildSignalFiltersPayloadParams): SignalEnrichmentFiltersPayload | undefined => {
    let intentPayload: SignalEnrichmentFiltersPayload["intent"] = null;
    let technologyPayload: SignalEnrichmentFiltersPayload["technology"] = null;

    if (intent && intent.ids.length > 0) {
        const [parentCategories, categories, topics] = bucketByLevel(intent, "intent");

        intentPayload = { parentCategories, categories, topics };
    }

    if (technology && technology.ids.length > 0) {
        const [categories, subCategories, technologies] = bucketByLevel(technology, "technology");

        technologyPayload = { categories, subCategories, technologies };
    }

    if (intentPayload) {
        return { intent: intentPayload, technology: technologyPayload };
    }

    if (technologyPayload) {
        return { intent: null, technology: technologyPayload };
    }

    return undefined;
};
